import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, Send, Instagram, Facebook } from 'lucide-react';
import { toast } from 'sonner';

const AboutPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [form, setForm] = useState({ name: '', email: '', message: '' });

  const submit = (e) => {
    e.preventDefault();
    if (form.name && form.email.includes('@') && form.message) {
      toast.success('Thank you! We will be in touch soon.');
      setForm({ name: '', email: '', message: '' });
    } else {
      toast.error('Please fill in all fields.');
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-[#3a4a3b]">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div className="text-[#faf7f1]">
            <div className="font-script text-2xl md:text-3xl text-[#dcd6c7] mb-3">Our story</div>
            <h1 className="font-serif-elegant text-5xl md:text-6xl leading-tight">About Herbs We Love</h1>
          </div>
          <div className="bg-[#faf7f1] p-2 shadow-2xl">
            <img
              src="/images/herbs-flatlay.webp"
              alt="Herbs, oils and botanicals of Herbs We Love"
              className="w-full h-auto object-contain block"
            />
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-4xl mx-auto px-6 py-24 text-center">
        <h2 className="section-title text-4xl md:text-5xl mb-8">Herbs. Healing. Heart.</h2>
        <p className="text-[#4a4640] leading-relaxed text-lg mb-6">
          Herbs We Love was born from a simple belief: nature already provides what we need to feel our best. We take a holistic approach to preventing and reversing disease through natural wellness solutions.
        </p>
        <p className="text-[#4a4640] leading-relaxed text-lg">
          Our range of holistic products, including CBD and various herbal products, focuses on treating you as a whole, not just your condition. We believe you cannot live your best life possible unless your mind and body are completely healthy.
        </p>
      </section>

      {/* Values */}
      <section className="bg-[#efe9db]">
        <div className="max-w-6xl mx-auto px-6 py-24 grid md:grid-cols-3 gap-10">
          {[
            { title: 'Holistic', body: 'We treat the whole person \u2014 body, mind, and spirit.' },
            { title: 'Handcrafted', body: 'Every product is made in small batches with intention.' },
            { title: 'Honest', body: 'Real ingredients. Real remedies. No shortcuts.' },
          ].map((v, i) => (
            <div key={v.title} className="text-center">
              <div className="font-script text-3xl text-[#7a8b6b] mb-2">0{i + 1}</div>
              <div className="font-serif-elegant text-3xl mb-3">{v.title}</div>
              <p className="text-[#4a4640]">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Legacy */}
      <section className="max-w-6xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-14 items-center">
        <div className="w-full bg-[#faf7f1] p-2 shadow-lg">
          <img
            src="/images/herbalist-team.webp"
            alt="The Herbs We Love team"
            className="w-full h-auto object-contain block"
          />
        </div>
        <div>
          <div className="uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-3">Willow Creek Springs</div>
          <h2 className="section-title text-4xl md:text-5xl mb-6">A Legacy of Natural Care</h2>
          <p className="text-[#4a4640] leading-relaxed mb-6">
            For over 20 years, our trusted herbal partners at Willow Creek Springs have been dedicated to creating all-natural skincare and wellness products. Powerful ingredients like turmeric, cayenne, ginger, rosemary and primrose oil are chosen for their proven benefits.
          </p>
          <Link to="/collections/all" className="btn-primary">Shop the Collection</Link>
        </div>
      </section>

      {/* Contact */}
      <section className="bg-[#3a4a3b] text-[#f6f2ea]">
        <div className="max-w-6xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-14">
          <div>
            <div className="uppercase tracking-[0.35em] text-xs text-[#dcd6c7] mb-3">Say hello</div>
            <h2 className="font-serif-elegant text-4xl md:text-5xl mb-6">We&apos;d love to hear from you</h2>
            <p className="text-[#e6e0d0] leading-relaxed mb-8">
              Questions about a product? Want to share your wellness story? Reach out &mdash; we read every message and reply personally.
            </p>
            <ul className="space-y-4 text-sm">
              <li className="flex items-center gap-3"><Mail className="w-5 h-5 shrink-0" /> <a href="mailto:connectwithanherbalist@outlook.com" className="link-underline break-all">connectwithanherbalist@outlook.com</a></li>
              <li className="flex items-center gap-3"><Phone className="w-5 h-5 shrink-0" /> <a href="tel:+19169088030" className="link-underline">(916) 908-8030</a></li>
              <li className="flex items-center gap-3"><MapPin className="w-5 h-5 shrink-0" /> Perris, CA</li>
            </ul>
            <div className="flex gap-4 mt-8">
              <a href="https://www.instagram.com/herbswelove?igsh=NTc4MTIwNjQ2YQ%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-white transition-colors"><Instagram className="w-5 h-5" /></a>
              <a href="https://www.facebook.com/profile.php?id=61577758914367&mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-white transition-colors"><Facebook className="w-5 h-5" /></a>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <input
              placeholder="Your name"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full bg-transparent border border-[#dcd6c7]/40 px-4 py-3 text-sm placeholder:text-[#dcd6c7]/70 focus:outline-none focus:border-[#dcd6c7]"
            />
            <input
              type="email"
              placeholder="your@email.com"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              className="w-full bg-transparent border border-[#dcd6c7]/40 px-4 py-3 text-sm placeholder:text-[#dcd6c7]/70 focus:outline-none focus:border-[#dcd6c7]"
            />
            <textarea
              placeholder="Your message"
              rows="5"
              value={form.message}
              onChange={e => setForm({ ...form, message: e.target.value })}
              className="w-full bg-transparent border border-[#dcd6c7]/40 px-4 py-3 text-sm placeholder:text-[#dcd6c7]/70 focus:outline-none focus:border-[#dcd6c7] resize-none"
            />
            <button type="submit" className="btn-outline !border-[#dcd6c7] !text-[#dcd6c7] hover:!bg-[#dcd6c7] hover:!text-[#3a4a3b] inline-flex items-center gap-2">
              Send Message <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
